/* ========================================================================= *
 * THE LAST HUNT, AND WHETHER ITS CROWNS REACHED A REP (Brian 2026-10-26).
 *
 *   "i see this on the command center but i dont see on a daily basis the
 *    last hunt?"
 *   "every crown the last hunt lands must show up on the assigned rep's
 *    worklist with its action, without waiting for a manual push... If a
 *    crown lands without reaching a worklist, flag it."
 *
 * PURE. No React, no fetch. The server (`GET /api/hunt/last-run`) says what
 * the hunt WROTE; this file says whether each crown is actually ON the
 * worklist a rep opens, and it decides that by running the worklist's OWN
 * ranking (`rank()` in repWorklist.js) over the same universe the worklist
 * reads. There is no second copy of "what is on a worklist" here: if the
 * worklist rule changes, this answer changes with it.
 * ========================================================================= */
import { ownerName } from './championships.js'
import { rank, hasChampion, TIER_BY_KEY } from '../features/command-center/worklist/repWorklist.js'
import { isPriorSeason } from './championships.js'

const S = (v) => (v == null ? '' : String(v)).trim()

/** Where a worklist row lives. MyWorklist reads `person` and `focus`. */
export const worklistHref = (rep, recordId) =>
  `/my-worklist?person=${encodeURIComponent(rep)}&focus=${encodeURIComponent(String(recordId))}`
export const recordHref = (recordId) => `/command-center?record=${encodeURIComponent(String(recordId))}`

/**
 * Every reach state, its words, and whether it is a FLAG (the crown is not
 * in front of anybody). A flag is never rendered in the good-news colour.
 */
export const REACH = Object.freeze({
  on_worklist: { flag: false, tone: 'ok', words: 'On the worklist' },
  held_back: { flag: true, tone: 'warn', words: 'On the worklist, but below the rows it shows' },
  prior_season: { flag: true, tone: 'warn', words: 'Held out of the worklist as a prior season' },
  unassigned: { flag: true, tone: 'alarm', words: 'On NO worklist: nobody is assigned' },
  owner_off_roster: { flag: true, tone: 'alarm', words: 'Assigned name absent from this build’s bundled roster; verify current membership' },
  not_on_board: { flag: true, tone: 'alarm', words: 'On NO worklist: the board does not carry this record' },
  board_not_refreshed: { flag: false, tone: 'pending', words: 'Crowned after the board was read; it appears on the next read' },
  report_read_failed: { flag: false, tone: 'alarm', words: 'Latest hunt read failed; current reach is unconfirmed' },
  report_pending: { flag: false, tone: 'pending', words: 'Hunt report is loading; current reach is unconfirmed' },
  report_incomplete: { flag: false, tone: 'warn', words: 'Hunt source evidence is incomplete; current reach is unconfirmed' },
  report_not_durable: { flag: false, tone: 'warn', words: 'Durable hunt evidence is unconfirmed; current reach is unconfirmed' },
  board_not_loaded: { flag: false, tone: 'pending', words: 'Full board read incomplete or unavailable; reach not checked yet' },
})

/**
 * For each crown, does it reach a rep, and where?
 *
 * @param crowns   [{ recordId, ... }] from the last-run read
 * @param universe the normalized records the Command Center has loaded
 * @param roster   names a worklist can be opened for (team roster + scope)
 * @param complete false while the universe is still paging in: then nothing
 *                 is called missing, because "not loaded yet" is not "absent"
 */
export function worklistReach(crowns = [], universe = [], { now = new Date(), roster = [], complete = true } = {}) {
  const byId = new Map()
  for (const c of universe || []) if (c && c.id != null) byId.set(String(c.id), c)
  const onRoster = new Set((roster || []).map((n) => S(n).toLowerCase()).filter(Boolean))
  const books = new Map()
  const rankFor = (rep) => {
    if (!books.has(rep)) {
      const want = rep.toLowerCase()
      const book = (universe || []).filter((c) => ownerName(c).toLowerCase() === want)
      /* The SAME call MyWorklist's view makes, minus the money table: the
       * crown tier is pinned ahead of revenue (see rank()), so the units
       * table cannot move a crown out of the rows. */
      books.set(rep, rank(book, { now, universe, orderBy: 'urgency' }))
    }
    return books.get(rep)
  }

  return (crowns || []).map((crown) => {
    const id = S(crown.recordId)
    const rec = byId.get(id)
    const base = { ...crown, recordId: id, recordHref: recordHref(id) }
    if (!complete) return { ...base, reach: 'board_not_loaded' }
    if (!rec) return { ...base, reach: 'not_on_board' }
    if (!hasChampion(rec)) return { ...base, reach: 'board_not_refreshed', rep: ownerName(rec) || null }
    const rep = ownerName(rec)
    if (!rep) return { ...base, reach: 'unassigned' }
    if (onRoster.size && !onRoster.has(rep.toLowerCase())) return { ...base, reach: 'owner_off_roster', rep }
    if (isPriorSeason(rec, now)) return { ...base, reach: 'prior_season', rep, worklistHref: worklistHref(rep, id) }
    const r = rankFor(rep)
    const row = r.rows.find((x) => String(x.id) === id || x.record?.memberRecords?.some((member) => String(member.id) === id))
    if (!row) return { ...base, reach: 'held_back', rep, worklistHref: worklistHref(rep, id), shown: r.rows.length, bookSize: r.bookSize }
    return { ...base, reach: 'on_worklist', rep, worklistHref: worklistHref(rep, row.id),
      tier: row.tier, tierLabel: TIER_BY_KEY[row.tier]?.label || row.tier,
      position: r.rows.indexOf(row) + 1,
      /* The work the row offers, minus "open", which every row has. */
      actions: (row.actions || []).filter((a) => !a.disabled && a.kind !== 'open').map((a) => a.label),
      /* THE CONTACT ACTION, said plainly. The worklist row's call/email action
       * needs a contact on file; without one the row's only live action is
       * "Open the record" and the contact action reads blocked. Reported, not
       * hidden, so "on the worklist" is never read as "ready to call". */
      contactAction: contactActionOf(row.actions || []) }
  })
}

const CONTACT_KINDS = new Set(['call', 'email', 'load-contacts', 'contact-gap'])
export function contactActionOf(actions = []) {
  const a = actions.find((x) => CONTACT_KINDS.has(x.kind))
  if (!a) return null
  return { kind: a.kind, label: a.label, live: !a.disabled, why: a.why || null }
}

export function reachTally(rows = []) {
  const t = Object.fromEntries(Object.keys(REACH).map((k) => [k, 0]))
  for (const r of rows) t[r.reach] = (t[r.reach] || 0) + 1
  const flagged = rows.filter((r) => REACH[r.reach]?.flag)
  const noContact = rows.filter((r) => r.reach === 'on_worklist' && r.contactAction && !r.contactAction.live).length
  return { ...t, total: rows.length, flagged: flagged.length, onWorklist: t.on_worklist, noContact }
}

/**
 * The header chip. The server's line is the hunt; this adds the reach.
 * Tone order: alarm > warn > ok. A flagged crown can only make it worse.
 */
export function headerLine(report, reach = []) {
  if (!report) return { tone: 'pending', text: 'Last hunt: reading...' }
  const v = report.verdict || {}
  const level = v.level === 'alarm' ? 'alarm' : v.level === 'ok' ? 'ok' : v.level === 'unknown' ? 'warn' : 'warn'
  const t = reachTally(reach)
  let tone = level
  let suffix = ''
  if (t.total) {
    if (t.flagged) {
      suffix = ` · ${t.flagged} winner record${t.flagged === 1 ? '' : 's'} need worklist reach review`
      if (tone === 'ok') tone = 'warn'
      if (reach.some((r) => REACH[r.reach]?.tone === 'alarm')) tone = 'alarm'
    } else if (t.onWorklist === t.total) {
      suffix = ` · all ${t.total} on rep worklists`
    }
  }
  return { tone, text: `${S(v.line) || 'Last hunt: no line returned'}${suffix}` }
}

export function huntEvidenceReady(report) {
  return report?.ready === true && report.complete === true && report.durable === true
    && report.capped === false && Array.isArray(report.errors) && report.errors.length === 0
    && ['book', 'company'].includes(report.scope?.kind) && report.scope.appliedBeforeCounts === true
    && report.scope.basis === 'current_roster_and_canonical_assignment'
}

/** Retain the actual evidence gap rather than calling every failure a loading board.
 * Any unconfirmed read still suppresses positive positions/contact actions. */
export function displayedHuntReach(state, reach = []) {
  const report = state?.report
  const reason = state?.status === 'error' ? 'report_read_failed'
    : state?.status !== 'ok' ? 'report_pending'
      : !huntEvidenceReady(report) ? (report?.durable !== true ? 'report_not_durable' : 'report_incomplete')
        : null
  const boardPending = reach.some(row => ['board_not_loaded', 'board_not_refreshed'].includes(row.reach))
  if (!reason && !boardPending) return reach
  return reach.map(row => ({ ...row,
    reach: reason || (['board_not_loaded', 'board_not_refreshed'].includes(row.reach) ? row.reach : 'board_not_loaded'),
    position: null, contactAction: null,
  }))
}

/** A failed refresh is never a stale green all-clear, even with a prior report. */
export function readHeaderLine(state, reach = []) {
  if (state?.status === 'error') return {
    tone: 'alarm',
    text: `Last hunt: latest read failed (${state.error || 'unavailable'}). ${state.report ? 'Showing the previous report; current worklist reach is not confirmed.' : 'This is not an all-clear.'}`,
  }
  if (state?.status !== 'ok' || reach.some((row) => ['board_not_loaded', 'board_not_refreshed'].includes(row.reach))) return {
    tone: state?.report?.verdict?.level === 'alarm' ? 'alarm' : 'pending', text: `Last hunt: current read or full board evidence is pending. ${state?.report ? 'Previous report is not a current all-clear.' : 'Reading...'}`,
  }
  if (!huntEvidenceReady(state?.report)) return {
    tone: state?.report?.verdict?.level === 'alarm' ? 'alarm' : 'warn', text: 'Last hunt: source completeness or durable evidence is unconfirmed. This scoped report is not a current all-clear.',
  }
  return headerLine(state?.report, reach)
}
